import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'

import { useEffectOnce, usePathParam, useViewport } from '@chilecompra/react-kit'
import { Card, Pagination, Spinner, Typography } from '@chilecompra/react-kit/components'

import {
  CALL_AGILE_PURCHASE,
  PURCHASE_ORDER_STATUS_DICTIONARY,
  PURCHASE_ORDER_URL,
  QUOTE_STATUSES_DICTIONARY,
  TYPE_OFFER_DICTIONARY
} from '../../config/settings/constants'
import { REACT_APP_MERCADO_PUBLICO_BASE_URL } from '../../config/settings/environment'

import { searchStatusPO } from '../../modules/utils/searches'
import { topPage } from '../../modules/utils/topPage'
import { inIframe } from '../../modules/utils/inIframe'

import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'

import { onOpenCancelQuoteDialog } from '../CancelQuoteDialog/CancelQuoteDialog.actions'
import { onOpenQuoteExtendClosingDateDialog } from '../ExtendQuoteClosingDateDialog/ExtendQuoteClosingDateDialog.actions'
import { onSavePurchaseOrderCode } from '../PurchaseOrderStatus/PurchaseOrderStatus.actions'
import { onOpenQuoteBidSeeDetailDialog } from '../QuoteBidSeeDetailDialog/QuoteBidSeeDetailDialog.actions'
import {
  onCloseDeclarationInadmissibilityQuoteDialog,
  onShowDeclarationInadmissibilityQuoteDialog
} from '../QuoteDeclarationInadmissibilityDialog/QuoteDeclarationInadmissibilityDialog.actions'
import { onGetInitialValuesThunk } from '../QuoteStepOne/QuoteStepOne.actions'
import {
  onCloseSelectOfferDialog,
  onDeleteSelectedOffers,
  onDisableOtherOffer,
  onDisableOtherOfferTotal,
  onEnableOtherOffer,
  onLowesValueOfferThunk
} from '../SelectOfferDialog/SelectOfferDialog.actions'
import { onShowSendSellerQuoteDialog } from '../SendSellerQuoteDialog/SendSellerQuoteDialog.actions'
import {
  onDialogCloseSuccess,
  onDialogOpenSuccess,
  onGetQuoteBidSeeDetailThunk,
  onGetResultExcelThunk,
  onMakeSecondCallQuoteThunk
} from './QuoteBid.actions'
import { onGetQuoteSummaryThunk } from '../QuoteBasicInformation/QuoteBasicInformation.actions'
import { onShowDeclarationDesertedQuoteDialog } from '../QuoteDeclarationDesertedDialog/QuoteDeclarationDesertedDialog.actions'

import DeclarationDesertedQuoteDialog from '../QuoteDeclarationDesertedDialog/QuoteDeclarationDesertedDialog.container'
import DeclarationInadmissibilityQuoteDialog from '../QuoteDeclarationInadmissibilityDialog/QuoteDeclarationInadmissibilityDialog.container'
import Errors from '../../components/Custom/Errors'
import NoBids from './QuoteBid.noBids'
import QuoteBidding from './QuoteBid.bidding'
import QuoteBidFooter from './QuoteBid.footer'
import QuoteBidQuantity from './QuoteBid.bidQuantity'
import QuoteBidSecondCallDialog from './QuoteBid.SecondCalldialog'
import QuoteBidSelected from './QuoteBid.bidSelected'
import SelectOfferDialogContainer from '../SelectOfferDialog/SelectOfferDialog.container'

import { ArrowDownwardIcon, DownloadLink, GridContainer, GridItem, Root, Tag } from './QuoteBid.styles'

/**
 * The QuoteBid's container.
 */
const QuoteBid = () => {
  const PAGINATION_BOUNDARY_COUNT = 5
  const PAGINATION_DISABLE_FIRSTBUTTON = false
  const PAGINATION_DISABLE_LASTBUTTON = false

  const {
    size: { isDownToTiny }
  } = useViewport()
  const dispatch = useDispatch()
  const { isBuyer, isSeller } = useAuthorization()
  const quoteId = usePathParam('quoteId')

  const { loading: loadingQuoteBid, openSecondCall } = useSelector(state => state.quoteBid)
  const {
    basicInfo,
    bidding,
    countInadmissible,
    countPurchaseCanceled,
    loading: loadingBasicInfo,
    paginationMeta,
    selectedProviders,
    statusCallAgile
  } = useSelector(state => state.quoteBasicInformation)
  const {
    disableOtherOffer,
    disableOtherOfferTotal,
    errors,
    isOfferLowerPrice,
    loadingSelected,
    selectedOffers,
    isFailedSelected
  } = useSelector(state => state.selectOfferDialog)
  const { open: openDetail } = useSelector(state => state.quoteBidSeeDetailDialog)
  const { open: openAbilityAffidavit } = useSelector(state => state.sendSellerQuoteDialog)
  const { showSelectOfferDialog: openOffer } = useSelector(state => state.selectOfferDialog)
  const { open: openCancel } = useSelector(state => state.cancelQuoteDialog)

  const [noBidsLeft, setNoBidsLeft] = useState(false)

  const isInIframe = useMemo(() => inIframe(), [])

  const publishedSeller = isSeller && basicInfo.statusId === QUOTE_STATUSES_DICTIONARY.PUBLISHED
  const QuoteBiddingValidation =
    basicInfo.statusId === QUOTE_STATUSES_DICTIONARY.CLOSED ||
    basicInfo.statusId === QUOTE_STATUSES_DICTIONARY['Proveedor seleccionado'] ||
    basicInfo.statusId === QUOTE_STATUSES_DICTIONARY.CANCELLED ||
    basicInfo.statusId === QUOTE_STATUSES_DICTIONARY.DESERTED
  const isVisible =
    isBuyer ||
    (isSeller && basicInfo.statusId === QUOTE_STATUSES_DICTIONARY['Proveedor seleccionado']) ||
    (isSeller &&
      basicInfo.statusId === QUOTE_STATUSES_DICTIONARY.CLOSED &&
      statusCallAgile.call !== CALL_AGILE_PURCHASE.PRIMER_LLAMADO) ||
    basicInfo.statusId === QUOTE_STATUSES_DICTIONARY.CANCELLED ||
    basicInfo.statusId === QUOTE_STATUSES_DICTIONARY.DESERTED

  const countBiddingStatus = () => {
    let count = 0

    if (paginationMeta.totalPages > 1) {
      count = countInadmissible + countPurchaseCanceled
    }

    if (countInadmissible + countPurchaseCanceled === paginationMeta.totalItems) {
      return true
    } else {
      Object.values(bidding).forEach(bid => {
        const isPurchaseOrderCancelStatus = bid?.purchaseOrderStatus?.some(
          purchaseStatus => purchaseStatus.purchaseOrderEstatusCode === 9
        )
        if (bid.statusBidId === isPurchaseOrderCancelStatus) count++
      })

      if (paginationMeta.totalItems === count) {
        count = 0
        return true
      } else {
        count = 0
        return false
      }
    }
  }
  const isSelectedCheckBox = ({ valueCheck, offerId, typeOfferSelected, checked, byInadmissibility = false }) => {
    if (valueCheck) {
      dispatch(
        onLowesValueOfferThunk({
          idOffer: offerId,
          callback: () => processInadmissibilityCheck(byInadmissibility, checked, typeOfferSelected)
        })
      )
    } else {
      dispatch(onDeleteSelectedOffers({ offerId }))
      processInadmissibilityCheck(byInadmissibility, checked, typeOfferSelected)
    }
  }
  const checkboxValidator = (checked, typeOfferSelected) => {
    if (!checked) {
      dispatch(onDisableOtherOffer())
      if (typeOfferSelected !== TYPE_OFFER_DICTIONARY.PARCIAL) {
        dispatch(onDisableOtherOfferTotal())
      }
    } else {
      selectedOffers.length === 1 && dispatch(onEnableOtherOffer())
    }
  }
  const processInadmissibilityCheck = (inadmissibility, check, typeOffer) => {
    /**
     * if is by inadmissibility, reset then other offers selected
     */
    if (inadmissibility) {
      dispatch(onEnableOtherOffer())
    } else {
      checkboxValidator(check, typeOffer)
    }
  }

  const handleSeeDetail = bidId => {
    batch(() => {
      dispatch(onGetQuoteBidSeeDetailThunk({ bidId }))
      dispatch(onOpenQuoteBidSeeDetailDialog())
    })
  }
  const handleDownloadExcel = () => dispatch(onGetResultExcelThunk({ quoteId }))
  const handleClick = () => dispatch(onMakeSecondCallQuoteThunk({ quoteId }))
  const handleCancel = () => dispatch(onOpenCancelQuoteDialog())
  const handleOpenModalDeserted = () => dispatch(onShowDeclarationDesertedQuoteDialog())
  const handleSecondCall = () => dispatch(onDialogOpenSuccess())
  const handleSecondCallClose = () => dispatch(onDialogCloseSuccess())
  const handleOpenExtendClosingDateDialog = () => dispatch(onOpenQuoteExtendClosingDateDialog({ code: quoteId }))
  const handleAbilityAffidavit = bidId => {
    dispatch(onGetQuoteBidSeeDetailThunk({ bidId }))
    dispatch(onShowSendSellerQuoteDialog({ showAbilityAffidavit: true }))
  }
  const handleDeclarationInadmissibility = bidId => {
    dispatch(onGetQuoteBidSeeDetailThunk({ bidId }))
    dispatch(onShowDeclarationInadmissibilityQuoteDialog({ showDeclarationInadmissibility: true, quoteBid: bidId }))
  }
  const handleChangePage = (_, pageId) => {
    dispatch(onGetQuoteSummaryThunk({ quoteId, pageId }))
  }
  const handleOpenPurchaseOrderStatus = ({ id, code }) => {
    dispatch(onSavePurchaseOrderCode(id))
    window.open(REACT_APP_MERCADO_PUBLICO_BASE_URL.concat(PURCHASE_ORDER_URL).concat(code), '_blank')
  }
  const handleDeclarationDeserted = useCallback(() => {
    dispatch(onShowDeclarationInadmissibilityQuoteDialog())
  }, [])

  useEffectOnce(() => {
    batch(() => {
      dispatch(onCloseDeclarationInadmissibilityQuoteDialog())
      dispatch(onCloseSelectOfferDialog())
      dispatch(onGetInitialValuesThunk())
    })
  })
  useEffect(() => {
    setNoBidsLeft(countBiddingStatus())
  }, [basicInfo, bidding, paginationMeta])
  useEffect(() => {
    topPage(isInIframe, openDetail)
  }, [openDetail])

  useEffect(() => {
    topPage(isInIframe, openAbilityAffidavit)
  }, [openAbilityAffidavit])

  useEffect(() => {
    topPage(isInIframe, openOffer)
  }, [openOffer])

  useEffect(() => {
    topPage(isInIframe, openCancel)
  }, [openCancel])

  useEffect(() => {
    topPage(isInIframe, openSecondCall)
  }, [openSecondCall])

  if (publishedSeller) {
    return null
  }

  return (
    <Root>
      {isBuyer && basicInfo.statusId === 2 && (
        <QuoteBidQuantity
          quoteQuantity={paginationMeta.totalItems ?? 0}
          onExtendClosingDate={handleOpenExtendClosingDateDialog}
          statusCallAgile={statusCallAgile}
        />
      )}
      {(basicInfo.statusId === QUOTE_STATUSES_DICTIONARY['Proveedor seleccionado'] ||
        (basicInfo.statusId === QUOTE_STATUSES_DICTIONARY.CANCELLED &&
          searchStatusPO(selectedProviders, PURCHASE_ORDER_STATUS_DICTIONARY.SENT_SELLER))) &&
        selectedProviders &&
        Object.values(selectedProviders).map((selectedProvider, index) => (
          <QuoteBidSelected
            bidId={selectedProvider.bidId}
            firstRow={index === 0}
            key={selectedProvider.bidId}
            loading={loadingBasicInfo}
            onAbilityAffidavit={handleAbilityAffidavit}
            onSeeDetail={handleSeeDetail}
            openPurchaseOrderStatus={handleOpenPurchaseOrderStatus}
            purchaseOrder={selectedProvider.purchaseOrder}
            selectedProvider={selectedProvider}
            typeCurrency={basicInfo.currency}
          />
        ))}
      {QuoteBiddingValidation && (
        <GridContainer alignItems="flex-end" padding="41px 0 0">
          <GridItem xs={12}>
            <GridContainer padding="4px 0 0">
              <GridContainer>
                <GridItem xs={10}>
                  <GridItem container alignItems="center" xs={12}>
                    <Typography color="black1" fontSize="22px" fontWeight="bold" lineHeight="26px" variant="h3">
                      Listado de proveedores que cotizaron
                    </Typography>
                    <Tag color="primary" margin="0 0 0 24px" text={statusCallAgile.call} />
                  </GridItem>
                  {isBuyer && (
                    <GridContainer>
                      <GridItem xs={12} padding="10px 0 0">
                        <Typography color="black1" fontSize="14px" lineHeight="18px" variant="body2">
                          Recibiste
                          <strong>
                            &nbsp;{paginationMeta.totalItems}{' '}
                            {paginationMeta.totalItems > 1 ? 'cotizaciones' : 'cotización'}
                          </strong>
                          &nbsp;de proveedores que respondieron a tu Compra Ágil.
                        </Typography>
                      </GridItem>
                    </GridContainer>
                  )}
                </GridItem>
                <GridItem xs={2}>
                  {paginationMeta.totalItems > 0 && isBuyer && (
                    <GridContainer style={{ position: 'relative', top: '67%' }}>
                      <DownloadLink onClick={handleDownloadExcel}>
                        {loadingQuoteBid ? 'Descargando resultados...' : 'Descargar resultados en excel'}
                      </DownloadLink>
                      {loadingQuoteBid ? <Spinner size={15} /> : <ArrowDownwardIcon margin="auto 0" />}
                    </GridContainer>
                  )}
                </GridItem>
              </GridContainer>
            </GridContainer>
            <Errors errors={errors} />
            <br />
            {paginationMeta.totalItems > 0 && isVisible > 0 ? (
              Object.values(bidding)
                .sort((a, b) => a.order - b.order)
                .map(bid => (
                  <QuoteBidding
                    active={Object.values(selectedProviders).some(
                      selectedProvider => selectedProvider.bidId === bid.bidId
                    )}
                    amount={bid.amount}
                    bidId={bid.bidId}
                    businessName={bid.businessName}
                    companySize={bid.companySize}
                    description={bid.description}
                    disableOtherOffer={disableOtherOffer}
                    disableOtherOfferTotal={disableOtherOfferTotal}
                    dni={bid.dni}
                    inadmissibility={bid.inadmissibility}
                    isFailedSelected={isFailedSelected}
                    isSelectedBid={bid.isSelected}
                    key={bid.bidId}
                    loading={loadingSelected}
                    name={bid.name}
                    onAbilityAffidavit={handleAbilityAffidavit}
                    onDeclarationDeserted={handleDeclarationDeserted}
                    onDeclarationInadmissibility={handleDeclarationInadmissibility}
                    onSeeDetail={handleSeeDetail}
                    openPurchaseOrderStatus={handleOpenPurchaseOrderStatus}
                    purchaseOrderStatus={bid.purchaseOrderStatus}
                    regions={bid.region}
                    selectedCheckbox={isSelectedCheckBox}
                    selectedOffers={selectedOffers[0] === bid.bidId}
                    statusBidId={bid.statusBidId}
                    statusId={basicInfo.statusId}
                    typeCurrency={basicInfo.currency}
                    typeOffer={bid.typeOffer}
                    validityDate={bid.validityDate}
                  />
                ))
            ) : (
              <></>
            )}
            {paginationMeta.totalItems > 0 && isSeller && !isVisible ? (
              <GridContainer>
                <GridItem md={9} xs={12}>
                  <GridContainer padding="16px 0 0">
                    <GridItem xs={12}>
                      <Card color="blank" elevation="2">
                        <GridContainer>
                          <GridItem xs={12}>
                            <Typography
                              color="black1"
                              fontSize="20px"
                              fontWeight="black"
                              lineHeight="26px"
                              variant="h3"
                            >
                              Se recibieron {paginationMeta.totalItems}
                              {paginationMeta.totalItems > 1 ? ' cotizaciones' : ' cotización'}.
                            </Typography>
                          </GridItem>
                        </GridContainer>
                        <GridContainer padding="4px 0 0">
                          <GridItem xs={12}>
                            {isBuyer && paginationMeta.totalPages < 1 ? (
                              <Typography color="black1" fontSize="16px" lineHeight="20px" variant="body1">
                                Una vez finalizado el periodo de publicación, podrás revisar el detalle de cada
                                cotización.
                              </Typography>
                            ) : (
                              <Typography color="black1" fontSize="16px" lineHeight="20px" variant="body1">
                                Las cotizaciones están siendo revisadas y evaluadas por el organismo comprador.
                              </Typography>
                            )}
                          </GridItem>
                        </GridContainer>
                      </Card>
                    </GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem md={3} xs={12} />
              </GridContainer>
            ) : (
              <></>
            )}
            {paginationMeta.totalItems === 0 && <NoBids />}
          </GridItem>
        </GridContainer>
      )}
      <QuoteBidSecondCallDialog
        open={openSecondCall}
        handleSecondCallClose={handleSecondCallClose}
        handleClick={handleClick}
        quoteId={quoteId}
      />
      {isVisible && paginationMeta.totalPages > 1 && (
        <Pagination
          count={paginationMeta.totalPages}
          disabled={loadingBasicInfo}
          page={paginationMeta.currentPage}
          boundaryCount={PAGINATION_BOUNDARY_COUNT}
          disableFirstButton={PAGINATION_DISABLE_FIRSTBUTTON}
          disableLastButton={PAGINATION_DISABLE_LASTBUTTON}
          size={isDownToTiny ? 'small' : 'medium'}
          onChange={handleChangePage}
        />
      )}
      <QuoteBidFooter
        bidding={paginationMeta.totalItems > 0}
        disabledSubmit={selectedOffers.length > 0}
        inFirstCall={statusCallAgile.call === CALL_AGILE_PURCHASE.PRIMER_LLAMADO}
        isOfferLowerPrice={isOfferLowerPrice}
        loadingSelected={loadingSelected}
        noBidsLeft={noBidsLeft}
        onCancel={handleCancel}
        onOpenModalDeserted={handleOpenModalDeserted}
        onSecondCall={handleSecondCall}
        statusId={basicInfo.statusId}
      />

      <DeclarationInadmissibilityQuoteDialog onDeclaration={handleChangePage} />
      <DeclarationDesertedQuoteDialog />

      <SelectOfferDialogContainer />
    </Root>
  )
}

export default QuoteBid
